import { useApi } from 'api/hooks/useApi';
import { useState, useCallback, useEffect } from 'react';
import { getApiErrorMessage } from 'utils/apiError';

interface CourseProgress {
  id: string;
  userId: string;
  courseId: string;
  currentModule: string;
  currentLesson: string;
  completedLessons: string[];
  completedLessonsCount: number;
  totalLessonsCount: number;
  progressPercentage: number;
  isCourseCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UseProgressReturn {
  progress: CourseProgress | null;
  progressPercentage: number;
  loading: boolean;
  error: string | null;

  fetchProgress: () => Promise<void>;
  completeLesson: (lessonId: string) => Promise<void>;
  isLessonCompleted: (lessonId: string) => boolean;
}

export function useProgress(courseId: string): UseProgressReturn {
  const { request } = useApi();

  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProgress = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);

    const data = await request(
      `api/courses/${courseId}/progress`,
      { method: 'GET' }
    );

    console.log('data progress:', data);
    setProgress(data.response);
  } catch (err: any) {
    setError(getApiErrorMessage(err, 'Erro inesperado'));
  } finally {
    setLoading(false);
  }
  }, [courseId, request]);

  useEffect(() => {
    if (courseId) {
      fetchProgress();
    }
  }, [courseId, fetchProgress]);

  /**
   * POST - Marcar aula como concluída
   */
  const completeLesson = useCallback(
    async (lessonId: string) => {
      try {
        setLoading(true);
        setError(null);

        const response = await request(
          `api/courses/${courseId}/progress/lessons/complete`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ lessonId }),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(
            data?.message || 'Erro ao marcar aula como concluída'
          );
        }

        const data = await response.json();
        console.log("response",response)
        setProgress(data.response);
      } catch (err: any) {
        setError(getApiErrorMessage(err, 'Erro inesperado'));
      } finally {
        setLoading(false);
      }
    },
    [courseId, request]
  );

  /**
   * Verifica se uma aula está concluída
   */
  const isLessonCompleted = useCallback(
    (lessonId: string) => {
      if (!progress) return false;
      return progress.completedLessons.includes(lessonId);
    },
    [progress]
  );


  return {
    progress,
    progressPercentage: progress?.progressPercentage ?? 0,
    loading,
    error,
    fetchProgress,
    completeLesson,
    isLessonCompleted,
  };
}
